import { useEffect, useRef, useState } from 'react';
import type { FirmNoteTextRow } from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { firmSettingsService } from '@/core/services/firmSettings';
import { standardNoteEditorConfig } from '@/features/tr-reportsettings/noteEditorConfig';
import { Button } from '@/kit/Button';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { Select } from '@/kit/Select';
import { getNoteTitle } from './generalSettingsNoteCatalogue';

const SECTION_NAMES: Record<number, string> = {
  1: "Director's / Member's Report",
  2: "Accountant / Auditor's Report",
  3: 'Accounting Policy',
};

const LEGACY_LLP_TITLES = new Set([
  "Members' responsibilities statement",
  'Disclosure of information to auditors',
  'Auditors',
  'Small LLP exemptions',
  'Exemption from audit',
  "Members' responsibilities",
  'Special provisions',
  'Opinion',
  'Basis for opinion',
]);

const editorConfig = standardNoteEditorConfig();

/**
 * Default Note Text (BUG-001): the firm's own wording for each report note of this layout. A client that has not edited
 * a note reads this text in its accounts and on its own settings pages; Reset Default puts the catalogue wording back.
 */
export function FirmNoteTexts({ settingsKey }: { settingsKey: string }) {
  const [rows, setRows] = useState<FirmNoteTextRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [text, setText] = useState('');
  const alive = useRef(true);
  /** True while Save or Reset Default is in flight, so a second click cannot submit twice. */
  const [busy, setBusy] = useState(false);

  const idOf = (row: FirmNoteTextRow) => `${row.section}/${row.noteKey}`;
  const current = rows.find((row) => idOf(row) === selected) ?? null;

  const apply = (next: FirmNoteTextRow[], keep: string | null) => {
    const normalised = next.map((row) => {
      const canonicalTitle = getNoteTitle(row.noteKey, settingsKey);
      if (canonicalTitle && (!row.isCustomised || LEGACY_LLP_TITLES.has(row.name))) {
        return { ...row, name: canonicalTitle };
      }
      return row;
    });

    setRows(normalised);
    const pick = normalised.find((row) => idOf(row) === keep) ?? normalised[0] ?? null;
    setSelected(pick ? idOf(pick) : null);
    setText(pick?.content ?? '');
  };

  useEffect(() => {
    alive.current = true;
    firmSettingsService.getNoteTexts(settingsKey).then(
      (loaded) => alive.current && apply(loaded, null),
      (error) => alive.current && notifyError(error),
    );
    return () => {
      alive.current = false;
    };
    // Loads once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = (id: string | null) => {
    const row = rows.find((r) => idOf(r) === id);
    setSelected(id);
    setText(row?.content ?? '');
  };

  const save = () => {
    if (!current || busy) return;
    setBusy(true);
    firmSettingsService
      .saveNoteText(settingsKey, current.section, current.noteKey, text)
      .then(
        (saved) => {
          apply(saved, idOf(current));
          notifySuccess('Default note text saved successfully.');
        },
        (error) => notifyError(error),
      )
      .finally(() => {
        if (alive.current) setBusy(false);
      });
  };

  const reset = () => {
    if (!current || busy) return;
    setBusy(true);
    firmSettingsService
      .resetNoteText(settingsKey, current.section, current.noteKey)
      .then(
        (saved) => {
          apply(saved, idOf(current));
          notifySuccess('Default note text reset.');
        },
        (error) => notifyError(error),
      )
      .finally(() => {
        if (alive.current) setBusy(false);
      });
  };

  return (
    <div className="sidenav-body pt-10">
      <div className="sidenav-body-content p-0">
        <div className="form-field-title pb-10">
          <p>Default Note Text</p>
        </div>
        <p className="font-size-12 text-gray mb-1">
          This wording is the default for every client of this type that has not edited the note.
        </p>
        <div className="form-field">
          <p>Note</p>
          <Select
            className="w-550"
            items={rows.map((row) => ({
              value: idOf(row),
              label: `${SECTION_NAMES[row.section] ?? 'Note'} - ${row.name}${row.isCustomised ? ' (edited)' : ''}`,
            }))}
            bindLabel="label"
            bindValue="value"
            value={selected}
            clearable={false}
            onChange={(value) => choose((value as string | null) ?? null)}
          />
        </div>
        {current && (
          <>
            <div className="form-field textarea w-550">
              <RichTextEditor
                id="firmNoteText"
                value={text}
                onChange={(html) => setText(html ?? '')}
                config={editorConfig}
              />
            </div>
            <div className="d-flex gap-15 mt-1">
              <Button className="action-button primary-button" busy={busy} onClick={save}>
                Save
              </Button>
              <Button className="action-button secondary-button" disabled={!current.isCustomised} onClick={reset}>
                Reset Default
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
